import styled from '@emotion/styled';
import { day as EDS_COLORS } from '@eventbrite/eds-settings';
import { gettext } from '@eventbrite/i18n';
import { LoadingSkeleton } from '@eventbrite/loading-skeleton';
import {
    Button,
    ChevronDownIcon,
    ChevronUpIcon,
    LoadingButton,
} from '@eventbrite/marmalade';
import React, { useEffect } from 'react';
import { useWindowSize } from 'react-use';
import { useAppContext } from '../../../AppContext';
import { SIGN_IN_PATH, TABLET_BREAKPOINT } from '../../../shared/constants';
import { useEventCreationProcess } from '../../Form/hooks';
import { MobileFloatingCard } from './MobileFloatingCard';
import {
    CardSection,
    Columns,
    EventDetails,
    LinkItems,
    TypographyWithSpacing,
} from './shared/components';
import { FloatingCardLinkItem } from './shared/types';

export enum FloatingCardTestId {
    Container = 'floatingCardContainer',
    Content = 'floatingCardContent',
    Close = 'floatingCardClose',
    Actions = 'floatingCardActions',
    Meeting = 'floatingCardMeeting',
    Publishing = 'publishProgressIndicator',
    PrimaryCTA = 'floatingCardPrimaryCTA',
    SecondaryCTA = 'floatingCardSecondaryCTA',
}

export const FloatingCard = () => {
    const [isOpen, setIsOpen] = React.useState(true);
    const [touched, setTouched] = React.useState(false);
    const [transitionIsActive, setTransitionActive] = React.useState(true);
    const { width } = useWindowSize();

    const {
        didCreateAccount,
        didCreateEvent,
        createdEventId,
        updateContext,
        isFullPageLoading,
        isModalShowing,
        isSubmittingCreateAccountForm,
    } = useAppContext();

    const { isCreatingOrganizationAndOrEvent, retryEventCreationProcess } =
        useEventCreationProcess();

    useEffect(() => {
        if (!isOpen) {
            setTimeout(() => setTransitionActive(false), 300);
        }
    }, [isOpen]);

    useEffect(() => {
        updateContext({
            isLoadingInInnerComponent: isCreatingOrganizationAndOrEvent,
        });
    }, [isCreatingOrganizationAndOrEvent, updateContext]);

    const isMobile = width < TABLET_BREAKPOINT;

    const renderCloseButton = !isMobile || (isMobile && !touched);

    const isInitialPageLoad =
        isFullPageLoading && !isCreatingOrganizationAndOrEvent;

    const isCreatingAccountInModal =
        !isModalShowing && isSubmittingCreateAccountForm;

    const shouldShimmerButtons = isInitialPageLoad || isCreatingAccountInModal;

    const shouldDisableLinks =
        isFullPageLoading ||
        isCreatingOrganizationAndOrEvent ||
        (didCreateAccount && !didCreateEvent);

    const content = didCreateAccount
        ? null
        : gettext('Create an account to publish your event.').toString();

    const primaryButtonText = !didCreateAccount
        ? gettext('Create account').toString()
        : gettext('Finalize event').toString();

    const primaryButtonClickHeapId = !didCreateAccount
        ? 'begin-account-creation-logged-out-auto-create-event'
        : 'finalize-event-logged-out-auto-create-event';

    const secondaryButtonClickHeapId =
        'edit-event-logged-out-auto-create-event';

    const hrefPreview = `/manage/events/${createdEventId}/preview_publish`;
    const hrefDetails = `/manage/events/${createdEventId}/details`;
    const hrefTickets = `/manage/events/${createdEventId}/tickets`;
    const hrefRefundPolicy = `/manage/events/${createdEventId}/refund_policy`;

    const linkItems: FloatingCardLinkItem[] = [
        {
            href: hrefDetails,
            text: gettext('Event images and video').toString(),
        },
        {
            href: hrefDetails,
            text: gettext('FAQ or Agenda').toString(),
        },
        {
            href: hrefTickets,
            text: gettext('Tickets or promo codes').toString(),
        },
        {
            href: hrefRefundPolicy,
            text: gettext('Review your refund policy').toString(),
        },
    ];

    const addedEventDetails = [
        gettext('Image, summary and description').toString(),
        gettext('Category and tags').toString(),
        gettext('General admission tickets').toString(),
    ];

    const toggle = () => {
        setIsOpen(!isOpen);
        setTouched(true);
    };

    const handlePrimaryAction = async () => {
        if (!didCreateAccount && !didCreateEvent) {
            // Redirect to the sign-in page
            window.open(SIGN_IN_PATH, '_self');
            return;
        }

        if (didCreateAccount && !didCreateEvent) {
            await retryEventCreationProcess();
            return;
        }

        window.open(hrefPreview, '_self');
    };

    const handleEditButtonClick = () => {
        window.open(hrefDetails, '_self');
    };

    return isMobile ? (
        <MobileFloatingCard
            isLoading={isCreatingOrganizationAndOrEvent}
            primaryButtonText={primaryButtonText}
            onPrimaryActionClick={handlePrimaryAction}
            primaryButtonClickHeapId={primaryButtonClickHeapId}
            secondaryButtonClickHeapId={secondaryButtonClickHeapId}
            onSecondaryActionClick={handleEditButtonClick}
            addedEventDetails={addedEventDetails}
            linkItems={linkItems}
            shouldShimmerButtons={shouldShimmerButtons}
            shouldDisableLinks={shouldDisableLinks}
            subtitle={content}
        />
    ) : (
        <Card data-testid={FloatingCardTestId.Container}>
            <Content isOpen={isOpen} transitionActive={transitionIsActive}>
                {renderCloseButton && (
                    <Close
                        type="button"
                        aria-label={gettext('Close').toString()}
                        onClick={toggle}
                        data-testid={FloatingCardTestId.Close}
                    >
                        {isOpen ? (
                            <ChevronDownIcon size="small" />
                        ) : (
                            <ChevronUpIcon size="small" />
                        )}
                    </Close>
                )}

                <TypographyWithSpacing
                    space={8}
                    as="h1"
                    variant="display-md"
                    color={'neutral-1000'}
                    style={{ visibility: isOpen ? 'visible' : 'hidden' }}
                >
                    {gettext('Your event is almost ready!').toString()}
                </TypographyWithSpacing>

                <TypographyWithSpacing
                    space={24}
                    as="h2"
                    variant="body-lg"
                    color={'neutral-1000'}
                    style={{ visibility: isOpen ? 'visible' : 'hidden' }}
                >
                    {content}
                </TypographyWithSpacing>

                <Columns
                    data-testid={FloatingCardTestId.Content}
                    isOpen={isOpen}
                    isLoading={shouldDisableLinks}
                    aria-hidden={shouldDisableLinks}
                    onClick={
                        !createdEventId && !didCreateAccount
                            ? (e: React.MouseEvent<HTMLDivElement>) => {
                                  updateContext({ isModalShowing: true });
                                  e.preventDefault();
                              }
                            : undefined
                    }
                >
                    <CardSection
                        title={gettext('What we included: ').toString()}
                        body={<EventDetails details={addedEventDetails} />}
                    />

                    <CardSection
                        title={gettext('Add more to your event: ').toString()}
                        body={<LinkItems links={linkItems} />}
                    />
                </Columns>
            </Content>

            <ActionBar
                isOpen={isOpen}
                isFull={!didCreateEvent}
                transitionActive={transitionIsActive}
                data-testid={FloatingCardTestId.Actions}
            >
                <Action isOpen={isOpen} isFull={!didCreateEvent}>
                    {shouldShimmerButtons ? (
                        <LoadingSkeleton height={'40px'} width={'100%'} />
                    ) : (
                        <LoadingButton
                            isLoading={isCreatingOrganizationAndOrEvent}
                            disabled={isCreatingOrganizationAndOrEvent}
                            data-testid={FloatingCardTestId.PrimaryCTA}
                            data-heap-id={primaryButtonClickHeapId}
                            variant="primary"
                            onClick={handlePrimaryAction}
                        >
                            {primaryButtonText}
                        </LoadingButton>
                    )}
                </Action>

                {didCreateEvent && (
                    <Action isOpen={isOpen} isFull={!didCreateEvent}>
                        {shouldShimmerButtons ? (
                            <LoadingSkeleton height={'40px'} width={'100%'} />
                        ) : (
                            <Button
                                disabled={isCreatingOrganizationAndOrEvent}
                                data-testid={FloatingCardTestId.SecondaryCTA}
                                data-heap-id={secondaryButtonClickHeapId}
                                variant="secondary"
                                onClick={handleEditButtonClick}
                            >
                                {gettext('Edit event')}
                            </Button>
                        )}
                    </Action>
                )}
            </ActionBar>
        </Card>
    );
};

const Card = styled.div`
    position: fixed;
    z-index: 10;
    bottom: 0;
    right: 0;
    left: 0;
    animation-duration: 0.5s;
    animation-name: animation-in;
    animation-delay: 0.5s;
    animation-fill-mode: backwards;

    @media (min-width: 740px) and (hover: hover) {
        max-width: 500px;
        left: auto;
        bottom: 24px;
        right: 24px;
        width: 100%;
    }

    @keyframes animation-in {
        0% {
            transform: translateY(500px);
        }
        100% {
            transform: translateY(0);
        }
    }
`;

const Content = styled.div<{
    isOpen: boolean;
    transitionActive: boolean;
}>`
    background-color: ${EDS_COLORS['ui-200']};
    background-image: url(https://cdn.evbstatic.com/s3-build/perm_001/7b5b78/django/images/create/auto-creation/auto-create-floating-card-splatter1.png),
        url(https://cdn.evbstatic.com/s3-build/perm_001/72dbfb/django/images/create/auto-creation/auto-create-floating-card-splatter2.png);
    background-repeat: no-repeat, no-repeat;
    background-position: top right, top right;
    border-radius: 8px;
    box-shadow: 0px 4px 15px rgba(40, 44, 53, 0.06),
        0px 2px 2px rgba(40, 44, 53, 0.06);
    padding: 72px 32px 120px;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    transition: all 0.3s ease-in-out;
    transform: ${(props) =>
        props.isOpen ? 'translateY(0%)' : 'translateY(100%)'};

    @media (min-width: 740px) and (hover: hover) {
        transform: ${(props) =>
            props.isOpen ? 'translateY(0%)' : 'translateY(85%)'};
    }
`;

const ActionBar = styled.ul<{
    isOpen: boolean;
    isFull: boolean;
    transitionActive: boolean;
}>`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    gap: 16px;
    flex-direction: column;
    background-color: ${EDS_COLORS['ui-200']};
    border-radius: 8px 8px 0 0;
    transition: ${(props) =>
        props.transitionActive ? 'all 0.3s ease-in-out' : 'none'};
    position: relative;
    z-index: 5;

    @media (min-width: 740px) and (hover: hover) {
        flex-direction: row;
        display: inline-flex;
        background-color: transparent;
        border-radius: 0;
        padding: ${(props) =>
            props.isOpen ? (props.isFull ? '32px' : '32px 24px') : '0 32px'};
    }
`;

const Action = styled.li<{ isOpen: boolean; isFull: boolean }>`
    width: ${(props) =>
        props.isOpen
            ? props.isFull
                ? '440px'
                : '220px'
            : props.isFull
            ? '400px'
            : '200px'};
    transition: width 0.3s ease-in-out;

    button {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;

const Close = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    background: rgba(248, 247, 250, 1);
    border-radius: 50%;
    border: none;
    padding: 0;
    width: 43px;
    height: 43px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0px 1px 17px rgba(40, 44, 53, 0.1),
        0px 2px 5px rgba(40, 44, 53, 0.1);
`;
